"use client";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Mail, Bell, Shield, Database, Save } from "lucide-react";

export default function SettingsPage() {
    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div>
                <h1 className="heading-sm text-primary">ตั้งค่าระบบ</h1>
                <p className="text-muted-foreground">กำหนดค่าพื้นฐานของระบบร้องเรียน</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* Email Settings */}
                <Card>
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                <Mail className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                                <CardTitle className="text-base">การแจ้งเตือนอีเมล</CardTitle>
                                <CardDescription>ตั้งค่าการส่งอีเมลแจ้งเตือน</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label>SMTP Server</Label>
                            <Input defaultValue="smtp.customs.go.th" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Port</Label>
                                <Input defaultValue="587" />
                            </div>
                            <div className="space-y-2">
                                <Label>Encryption</Label>
                                <Select defaultValue="tls">
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">None</SelectItem>
                                        <SelectItem value="ssl">SSL</SelectItem>
                                        <SelectItem value="tls">TLS</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>อีเมลผู้ส่ง</Label>
                            <Input defaultValue="noreply@customs.go.th" />
                        </div>
                    </CardContent>
                </Card>

                {/* Notification Settings */}
                <Card>
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                <Bell className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                                <CardTitle className="text-base">การแจ้งเตือน</CardTitle>
                                <CardDescription>กำหนดเงื่อนไขการแจ้งเตือน</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label>แจ้งเตือนก่อนครบ SLA (ชั่วโมง)</Label>
                            <Input type="number" defaultValue="8" />
                        </div>
                        <div className="space-y-2">
                            <Label>ส่งสรุปประจำวันเวลา</Label>
                            <Input type="time" defaultValue="08:00" />
                        </div>
                        <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                            <div>
                                <p className="font-medium text-sm">แจ้งเตือนผู้บริหารเมื่อเกิน SLA</p>
                                <p className="text-xs text-muted-foreground">ส่งอีเมลถึงผู้บริหารทันที</p>
                            </div>
                            <input type="checkbox" defaultChecked className="h-4 w-4" />
                        </div>
                    </CardContent>
                </Card>

                {/* SLA Default Settings */}
                <Card>
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                <Shield className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                                <CardTitle className="text-base">ค่าเริ่มต้น SLA</CardTitle>
                                <CardDescription>กำหนดค่าเริ่มต้นสำหรับประเภทใหม่</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label>ระยะเวลาเริ่มต้น (ชั่วโมง)</Label>
                            <Input type="number" defaultValue="48" />
                        </div>
                        <div className="space-y-2">
                            <Label>วันทำการ</Label>
                            <Select defaultValue="weekdays">
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">ทุกวัน</SelectItem>
                                    <SelectItem value="weekdays">จันทร์-ศุกร์</SelectItem>
                                    <SelectItem value="custom">กำหนดเอง</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>เวลาทำการ</Label>
                            <div className="grid grid-cols-2 gap-2">
                                <Input type="time" defaultValue="08:30" />
                                <Input type="time" defaultValue="16:30" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* System Info */}
                <Card>
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                <Database className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                                <CardTitle className="text-base">ข้อมูลระบบ</CardTitle>
                                <CardDescription>สถานะและเวอร์ชันของระบบ</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between py-2 border-b">
                                <span className="text-muted-foreground">เวอร์ชัน</span>
                                <span className="font-medium">1.0.0</span>
                            </div>
                            <div className="flex justify-between py-2 border-b">
                                <span className="text-muted-foreground">สถานะฐานข้อมูล</span>
                                <span className="text-green-600 font-medium">เชื่อมต่อแล้ว</span>
                            </div>
                            <div className="flex justify-between py-2 border-b">
                                <span className="text-muted-foreground">พื้นที่จัดเก็บ</span>
                                <span className="font-medium">2.4 GB / 100 GB</span>
                            </div>
                            <div className="flex justify-between py-2">
                                <span className="text-muted-foreground">อัปเดตล่าสุด</span>
                                <span className="font-medium">14 ม.ค. 2569</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Separator />

            {/* Save Button */}
            <div className="flex justify-end">
                <Button>
                    <Save className="mr-2 h-4 w-4" />
                    บันทึกการตั้งค่า
                </Button>
            </div>
        </div>
    );
}
